import React from 'react';
import { ReactComponent as Cross } from '../../webcommon/common/Icons/Cross-Disabled.svg';
import fp from 'lodash/fp';
import TextInput from '../../webcommon/common/formcomponents/textinput/TextInput';
import Dropdown from '../../webcommon/common/formcomponents/dropdown/Dropdown';
import TypeAhead from '../../webcommon/common/formcomponents/typeahead/Typeahead';
import WithTooltip from '../../webcommon/common/formcomponents/customvalidation/ComponentWithTooltip';
import { SingleDatePicker } from 'react-dates';
import { dateTimeToMoment } from '../../webcommon/common/helpers';
import { dateDisplayFormat, dateTransferFormat } from '../../webcommon/common/constants';
import OnRowClick from '../../common/customgridcomponents/OnRowClick';
import textBoxStyles from './styles/TextBoxOverride.module.scss';
import textInputStyles from '../../common/customgridcomponents/TextInputStyles.module.scss';
import positiveAmountStyles from './styles/TextBoxAmountPositiveStyles.module.scss';
import negativeAmountStyles from './styles/TextBoxAmountNegativeStyles.module.scss';
import 'react-dates/lib/css/_datepicker.css';
import datesPickerStyles from './styles/DatePickerStyles.module.scss';
import textInputErrorStyles from './styles/TextInputErrorStyles.module.scss';
import TextInputErrorStylesAmount from './styles/TextInputErrorStylesAmount.module.scss';
import wrapperStyles from './styles/WrapperStyles.module.scss';
import TradeIdHeader from './customgridcomponents/TradeIdHeader';

const toolTipString = 'Invalid entry. Please select a valid choice from the drop-down';

const TextInputWithValidation = WithTooltip(TextInput);
const DropDownWithValidation = WithTooltip(Dropdown);

const findDefaultBucketForQualifier = (row, name, rowId) => {
    const activeImModel = row.column.getGridProps().rows
        .find(x => x.id === rowId).imModel;

    const availableValues = row.column.getGridProps().rowMappings.crifModelMetaData
        .find(option => option.imModel === activeImModel);

    const activeRiskType = row.column.getGridProps().rows
        .find(x => x.id === rowId).riskType;

    if (availableValues && availableValues.riskTypeMetaDataList[activeRiskType]) {
        const activeQualifier = availableValues.riskTypeMetaDataList[activeRiskType]
            .qualifiers.find(qual => qual.name === name);
        return activeQualifier ? activeQualifier.value : '';
    }
    return '';
};

const changeActiveValue = (key, value, row, setDefaultBucketWhenPossible) => {
    if (value !== row.value) {
        row.column.getGridProps()
            .setActiveValue(key, value, row.original.id);
    }

    if (setDefaultBucketWhenPossible) {
        const defaultBucket = findDefaultBucketForQualifier(row, value, row.original.id);
        if (defaultBucket) {
            row.column.getGridProps()
                .setActiveValue('bucket', defaultBucket, row.original.id);
        }
    }

    if (key === 'prodClass') {
        const { productDefaults } = row.column.getGridProps().rowMappings;
        if (productDefaults !== undefined) {
            const imModel = productDefaults[value];
            if (imModel) {
                row.column.getGridProps().setActiveValue('imModel', imModel, row.original.id);
            } else {
                const { productList } = row.column.getGridProps().rowMappings;
                const availableOptions = fp.get(value, productList);
                if (availableOptions && availableOptions.length) {
                    row.column.getGridProps().setActiveValue('imModel', availableOptions[0], row.original.id);
                }
            }
        }
    }

    const {
        riskType, labelOne, labelTwo, qualifier
    } = row.column.getGridProps().rows
        .find(x => x.id === row.original.id);

    // Fix all duplicates of the error in other rows
    if (key === 'labelOne') {
        row.column.getGridProps().gridErrorsAndOptions.filter(
            x => x.riskType && x.riskType.value === riskType && x.labelOne && x.labelOne.error
            && x.labelOne.value === labelOne
        ).map(x => x.id && row.column.getGridProps().setActiveValue(key, value, x.id.value));
    }

    if (key === 'labelTwo') {
        row.column.getGridProps().gridErrorsAndOptions.filter(
            x => x.riskType && x.riskType.value === riskType && x.labelTwo && x.labelTwo.error
            && x.labelTwo.value === labelTwo
        ).map(x => x.id && row.column.getGridProps().setActiveValue(key, value, x.id.value));
    }

    if (key === 'qualifier') {
        row.column.getGridProps().gridErrorsAndOptions.filter(
            x => x.qualifier && x.qualifier.error && x.qualifier.value === qualifier
        ).map(x => x.id && row.column.getGridProps().setActiveValue('bucket',
            findDefaultBucketForQualifier(row, value, x.id.value), x.id.value)
        && row.column.getGridProps().setActiveValue(key, value, x.id.value));
    }
};

const RenderTextInput = (row, key) => (
    <TextInput
        value={ row.column.getGridProps().rows
            .find(x => x.id === row.original.id)[key] }
        onChange={ (e) => {
            changeActiveValue(key, e.target.value, row);
        } }
        inputProps={ {
            onBlur: (e) => {
                changeActiveValue(key, e.target.value, row);
            }
        } }
        placeholder="..."
        styles={ textBoxStyles }
    />
);

const RenderDropdown = (row, columnsKey, changeDefaultBucket) => {
    const activeValue = row.column.getGridProps().rows
        .find(x => x.id === row.original.id)[columnsKey];
    const isErrorObj = row.column.getGridProps().gridErrorsAndOptions.find(
        x => x.id && x.id.value === row.original.id
    );
    const isError = isErrorObj && isErrorObj[columnsKey] && isErrorObj[columnsKey].error;

    const availableOptions = isErrorObj && isErrorObj[columnsKey]
    && isErrorObj[columnsKey].availableOptions;

    const inputIsDisabled = !availableOptions || !availableOptions.length;

    return (
        <DropDownWithValidation
            isError={ isError }
            showTooltip={ isError }
            onSelection={ e => changeActiveValue(columnsKey, e, row, changeDefaultBucket) }
            options={ availableOptions }
            placeholder=""
            textInputStyles={ isError ? textInputErrorStyles : textInputStyles }
            value={ activeValue }
            portal
            trigger={ ['hover', 'focus'] }
            placement="top"
            wrapperStyles={ wrapperStyles }
            tooltipMessage={ toolTipString }
            disabled={ inputIsDisabled }
            hideIcon={ inputIsDisabled }
        />
    );
};

const findAmountStyles = (isError, value) => {
    if (isError) {
        return TextInputErrorStylesAmount;
    }
    return value >= 0 ? positiveAmountStyles : negativeAmountStyles;
};


const findAmountTooltipMessage = (isError, value) => {
    if (isError) {
        return 'Please enter a valid amount';
    }
    return value === '' ? 'No value entered' : parseFloat(value).toLocaleString(undefined, { maximumFractionDigits: 10 });
};

const mappings = [
    {
        id: 'tradeId',
        title: 'Trade ID',
        renderCell: row => RenderTextInput(row, 'tradeId'),
        Header: header => (
            <TradeIdHeader
                onClick={ header.column.getGridProps().copyTradesIds }
                title={ header.column.title }
            />
        )
    },
    {
        id: 'prodClass',
        title: 'Prod Class',
        renderCell: row => RenderDropdown(row, 'prodClass', false)
    }, {
        id: 'imModel',
        title: 'IM Model',
        renderCell: row => RenderDropdown(row, 'imModel', false)
    },
    {
        id: 'riskType',
        title: 'Risk Type',
        renderCell: row => RenderDropdown(row, 'riskType', false)
    },
    {
        id: 'qualifier',
        title: 'Qualifier',
        renderCell: (row) => {
            const isErrorObj = row.column.getGridProps().gridErrorsAndOptions.find(
                x => x.id && x.id.value === row.original.id
            );

            const availableOptions = isErrorObj && isErrorObj.qualifier
                    && isErrorObj.qualifier.availableOptions;

            const isAnEditableQualifier = isErrorObj && isErrorObj.qualifier
            && isErrorObj.qualifier.availableOptions;

            if (isAnEditableQualifier === true) {
                const { riskType } = row.column.getGridProps().rows
                    .find(x => x.id === row.original.id);
                const isErrorObj = row.column.getGridProps().gridErrorsAndOptions.find(
                    x => x.id && x.id.value === row.original.id
                );
                const isError = isErrorObj && isErrorObj.qualifiers && isErrorObj.qualifiers.error;

                return (
                    <TextInputWithValidation
                        value={ row.column.getGridProps().rows
                            .find(x => x.id === row.original.id).qualifier }
                        onChange={ (e) => {
                            changeActiveValue('qualifier', e.target.value, row);
                        } }
                        inputProps={ {
                            onBlur: (e) => {
                                changeActiveValue('qualifier', e.target.value, row);
                                row.column.getGridProps()
                                    .getDefaultBucket(riskType, e.target.value, row.original.id);
                            }
                        } }
                        placeholder="..."
                        styles={ isError ? textInputErrorStyles : textBoxStyles }
                        wrapperStyles={ wrapperStyles }
                        showTooltip={ isError }
                    />
                );
            }
            if (row.column.getGridProps().rows
                .find(x => x.id === row.original.id).riskType === 'Risk_Commodity') {
                return (
                    <TypeAhead
                        allowFreeText
                        onSelection={ e => changeActiveValue('qualifier', e, row, true) }
                        options={ availableOptions }
                        placeholder=""
                        textInputStyles={ textInputStyles }
                        value={ row.column.getGridProps().rows
                            .find(x => x.id === row.original.id).qualifier }
                        portal
                    />
                );
            }
            return (
                RenderDropdown(row, 'qualifier', true)
            );
        }
    },
    {
        id: 'labelOne',
        title: 'Label 1',
        renderCell: row => RenderDropdown(row, 'labelOne', false)
    },
    {
        id: 'labelTwo',
        title: 'Label 2',
        renderCell: row => RenderDropdown(row, 'labelTwo', false)
    },
    {
        id: 'bucket',
        title: 'Bucket',
        renderCell: row => RenderDropdown(row, 'bucket', false)
    },
    {
        id: 'endDate',
        title: 'End Date',
        renderCell: (row) => {
            const activeValue = row.column.getGridProps().rows
                .find(x => x.id === row.original.id).date;
            const rowId = row.column.getGridProps().rows
                .find(x => x.id === row.original.id).id;
            const isDisabled = row.column.getGridProps().rows
                .find(x => x.id === row.original.id).imModel !== 'Schedule';

            return (
                <div className={ datesPickerStyles['dates-wrapper'] }>
                    <SingleDatePicker
                        date={ activeValue ? dateTimeToMoment(activeValue) : null }
                        disabled={ isDisabled }
                        onDateChange={ date => (date !== null ? changeActiveValue('date', date.format(dateTransferFormat),
                            row) : changeActiveValue('date', null, row)) }
                        displayFormat={ dateDisplayFormat }
                        id={ `date${rowId}` }
                        isOutsideRange={ () => false }
                        numberOfMonths={ 1 }
                        firstDayOfWeek={ 1 }
                        showClearDate
                        focused={ row.column.getGridProps().rows
                            .find(x => x.id === row.original.id).id
                             === row.column.getGridProps().dateRowBeingFocused }
                        onFocusChange={ ({ focused }) => row.column.getGridProps()
                            .setDateRowBeingFocused(rowId, focused) }
                        small
                        hideKeyboardShortcutsPanel
                        appendToBody
                        placeholder={ isDisabled ? '' : 'Date' }
                    />
                </div>
            );
        }
    },
    {
        id: 'amountCcy',
        title: 'Amount CCY',
        renderCell: row => (
            <Dropdown
                onSelection={ e => changeActiveValue('amountCcy', e, row) }
                options={ row.column.getGridProps().rowMappings.currencies }
                placeholder=""
                textInputStyles={ textInputStyles }
                value={ row.column.getGridProps().rows
                    .find(x => x.id === row.original.id).amountCcy }
                portal
            />
        )
    },
    {
        id: 'amount',
        title: 'Amount',
        renderCell: (row) => {
            const value = row.column.getGridProps().rows.find(x => x.id === row.original.id).amount;
            const currency = row.column.getGridProps().rows
                .find(x => x.id === row.original.id).amountCcy;

            const isErrorObj = row.column.getGridProps().gridErrorsAndOptions.find(
                x => x.id && x.id.value === row.original.id
            );
            const isError = isErrorObj && isErrorObj.amount && isErrorObj.amount.error;

            return (
                <TextInputWithValidation
                    value={ value }
                    disabled={ row.column.getGridProps().rows
                        .find(x => x.id === row.original.id).amountCcy === '' }
                    onChange={ (e) => {
                        changeActiveValue('amount', e.target.value, row);
                    } }
                    inputProps={ {
                        onBlur: (e) => {
                            row.column.getGridProps()
                                .deriveAmountUsd(value, currency, true, row.original.id);
                            changeActiveValue('amount', e.target.value, row);
                        }
                    } }
                    placeholder="..."
                    styles={ findAmountStyles(isError, value) }
                    showTooltip
                    tooltipMessage={ findAmountTooltipMessage(isError, value) }
                    wrapperStyles={ wrapperStyles }
                    getDisplayValue={ (value, isEditing) => (
                        isEditing || value === '' || isError ? value : Math.round(value).toLocaleString()
                    ) }
                />
            );
        }
    },
    {
        id: 'amountUsd',
        title: 'Amount (USD)',
        renderCell: (row) => {
            const value = row.column.getGridProps().rows
                .find(x => x.id === row.original.id).amountUsd;
            const currency = row.column.getGridProps().rows
                .find(x => x.id === row.original.id).amountCcy;
            const isErrorObj = row.column.getGridProps().gridErrorsAndOptions.find(
                x => x.id && x.id.value === row.original.id
            );
            const isError = isErrorObj && isErrorObj.amountUsd && isErrorObj.amountUsd.error;
            return (
                <TextInputWithValidation
                    value={ value }
                    onChange={ (e) => {
                        changeActiveValue('amountUsd', e.target.value, row);
                    } }
                    inputProps={ {
                        onBlur: (e) => {
                            row.column.getGridProps()
                                .deriveAmountUsd(value, currency, false, row.original.id);
                            changeActiveValue('amountUsd', e.target.value, row);
                        }
                    } }
                    placeholder="..."
                    styles={ findAmountStyles(isError, value) }
                    showTooltip
                    tooltipMessage={ findAmountTooltipMessage(isError, value) }
                    wrapperStyles={ wrapperStyles }
                    getDisplayValue={ (value, isEditing) => (
                        isEditing || value === '' || isError ? value : Math.round(value).toLocaleString()
                    ) }
                />
            );
        }
    },
    {
        id: 'deleteRow',
        title: '',
        renderCell: row => (
            <OnRowClick
                onClick={ row.column.getGridProps().deleteRow }
                id={ row.original.id }
            ><Cross />
            </OnRowClick>
        ),
        width: 50
    }
];

export default mappings;
